import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { OwcBanner, OwcButton, OwcTypography } from '@one/react';
import styled from 'styled-components';

// Styled container for positioning banners
const ErrorNotificationContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  max-width: 500px;
  min-width: 350px;
`;

// Container for each error banner with animation
const ErrorBannerWrapper = styled.div`
  margin-bottom: 12px;
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

// Styled content within the banner
const ErrorContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ErrorHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ClearAllContainer = styled.div`
  margin-top: 8px;
`;

// Global error storage - similar to how notifications might work
let globalErrors = [];
let errorListeners = [];
let errorTimeouts = new Map(); // Track timeouts for auto-dismiss

export const addGlobalError = (error, id = Date.now(), autoDismissSeconds = 7) => {
  const errorMessage =  `Error occured in ${id.split('-')[0]}`
  console.log(extractErrorMessage(error));
  if (errorMessage) {
    const newError = { 
      id, 
      message: errorMessage, 
      timestamp: Date.now()
    };
    globalErrors = [...globalErrors, newError];
    
    // Set auto-dismiss timeout
    if (autoDismissSeconds > 0) {
      const timeoutId = setTimeout(() => {
        removeGlobalError(id);
      }, autoDismissSeconds * 1000);
      
      errorTimeouts.set(id, timeoutId);
    }
    
    // Notify all listeners
    errorListeners.forEach(listener => listener(globalErrors));
  }
};

export const removeGlobalError = (id) => {
  // Clear the timeout if it exists
  if (errorTimeouts.has(id)) {
    clearTimeout(errorTimeouts.get(id));
    errorTimeouts.delete(id);
  }
  
  globalErrors = globalErrors.filter(error => error.id !== id);
  errorListeners.forEach(listener => listener(globalErrors));
};

export const clearAllGlobalErrors = () => {
  // Clear all timeouts
  errorTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
  errorTimeouts.clear();
  
  globalErrors = [];
  errorListeners.forEach(listener => listener(globalErrors));
};

const extractErrorMessage = (error) => {
  // Handle GraphQL errors from response
  if (error?.errors && Array.isArray(error.errors)) {
    return error.errors.map(err => err.message).join(', ');
  }
  
  // Handle GraphQL errors from Apollo/AppSync
  if (error?.graphQLErrors && Array.isArray(error.graphQLErrors)) {
    return error.graphQLErrors.map(err => err.message).join(', ');
  }
  
  // Handle network errors
  if (error?.networkError?.message) {
    return error.networkError.message;
  }
  
  // Handle generic error messages
  if (error?.message) {
    return error.message;
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }
  
  return null;
};

export const ErrorNotificationStatus = () => {
  const [errors, setErrors] = useState(globalErrors);
  const location = useLocation();
  
  // Check if current route is in the bookings feature
  const isBookingsRoute = location.pathname.includes('/bookings');
  
  useEffect(() => {
    // Subscribe to global error changes
    const listener = (newErrors) => {
      setErrors([...newErrors]);
    };
    
    errorListeners.push(listener);
    
    // Cleanup on unmount
    return () => {
      errorListeners = errorListeners.filter(l => l !== listener);
    };
  }, []);

  // Simple error banner component without countdown/progress
  const ErrorBanner = ({ error }) => {

    return (
      <ErrorBannerWrapper>
        <OwcBanner type="error" dismissible>
          <ErrorContent>
            <ErrorHeader>
              <OwcTypography variant="subtitle2" style={{ fontWeight: '600', color: '#d32f2f' }}>
                GraphQL Error
              </OwcTypography>
            </ErrorHeader>
            
            <OwcTypography variant="body2">
              {error.message}
            </OwcTypography>
            
            <OwcTypography variant="caption" style={{ color: '#666', marginTop: '8px' }}>
              {formatTimestamp(error.timestamp)}
            </OwcTypography>
          </ErrorContent>
        </OwcBanner>
      </ErrorBannerWrapper>
    );
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  // Don't render if no errors or not on bookings route
  if (errors.length === 0 || !isBookingsRoute) {
    return null;
  }

  return (
    <ErrorNotificationContainer>
      {errors.map((error) => (
        <ErrorBanner key={error.id} error={error} />
      ))}
      {errors.length > 1 && (
        <ClearAllContainer>
          <OwcButton 
            variant="secondary" 
            size="small" 
            onClick={clearAllGlobalErrors}
            style={{ width: '100%' }}
          >
            Clear All Errors ({errors.length})
          </OwcButton>
        </ClearAllContainer>
      )}
    </ErrorNotificationContainer>
  );
};
